// app/layout.client.tsx
"use client";

import { ReactNode, useEffect, useState } from "react";
import { ThemeProvider, useTheme } from "next-themes";
import { Analytics } from "@vercel/analytics/next";
import { usePathname } from "next/navigation";
import Nav from "@/components/ui/nav";
import Footer from "@/components/ui/footer";
import OAuthRedirectFix from "@/components/o-auth-redirect";
import ReverseWave from "@/components/svg/reverse-wave";
import MissionSection from "@/components/mission";
import PromoTicker from "@/components/promo";
import { AuthProvider } from "@/context/auth-context";
import { PriceProvider } from "@/context/price-context";
import { faqSections } from "@/fake_data/options";
import { ColorProvider } from "@/context/color-provider";

import Script from "next/script";
import "@/app/globals.css";

// Composant interne pour gérer le thème après le montage
function ThemedContent({ children }: { children: ReactNode }) {
  const { theme, systemTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const pathname = usePathname();
  const authPages = ["/sign-in", "/sign-up", "/reset-password", "/forgot-password"];
  const isAuthPage = authPages.includes(pathname);
  const isPaymentPage = pathname === "/payment";

  // Si pas encore monté, affichage minimal pour éviter l'hydration mismatch
  if (!mounted) {
    return (
      <div>
        <ColorProvider>
          <AuthProvider>
            <PriceProvider faqSections={faqSections}>
              <OAuthRedirectFix />
              {children}
            </PriceProvider>
          </AuthProvider>
        </ColorProvider>
      </div>
    );
  }

  // Une fois monté, appliquer le thème correctement
  const appliedTheme = theme === "system" ? systemTheme : theme;

  return (
    <div className={`${appliedTheme === "dark" ? "dark" : ""} w-full overflow-hidden`}>
      <ColorProvider>
        <AuthProvider>
          <PriceProvider faqSections={faqSections}>
            <OAuthRedirectFix />

            <Script
              id="google-tag-manager"
              strategy="beforeInteractive"
              dangerouslySetInnerHTML={{
                __html: `
                  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;
                  j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
                  f.parentNode.insertBefore(j,f);
                  })(window,document,'script','dataLayer','GTM-56NCRVT3');
                `,
              }}
            />

            {!isAuthPage && !isPaymentPage && <Nav />}
            {!isAuthPage && <PromoTicker />}

            <div className={`h-auto min-h-[71%] ${!isPaymentPage ? "topo" : ""}`}>
              {children}
              <Analytics />
              {!isAuthPage && !isPaymentPage && <MissionSection />}
              {!isAuthPage && !isPaymentPage && <ReverseWave />}
            </div>

            {!isAuthPage && !isPaymentPage && <Footer />}
          </PriceProvider>
        </AuthProvider>
      </ColorProvider>
    </div>
  );
}

export default function LayoutClient({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <ThemedContent>{children}</ThemedContent>
    </ThemeProvider>
  );
}